import { useCallback, useEffect, useState } from 'react'

export type Tema = 'claro' | 'escuro' | 'sistema'

const CHAVE_TEMA = 'paroquia:tema'
const CHAVE_ESCALA = 'paroquia:escala-texto'

/** Cor da barra do navegador em cada tema resolvido. */
const COR_DA_BARRA: Record<'claro' | 'escuro', string> = {
  claro: '#264776',
  escuro: '#0e1724',
}

export const ESCALAS_DE_TEXTO = [
  { valor: 1, label: 'A', titulo: 'Tamanho normal' },
  { valor: 1.15, label: 'A+', titulo: 'Texto maior' },
  { valor: 1.3, label: 'A++', titulo: 'Texto bem maior' },
] as const

/**
 * Preferencia salva, ou `claro` quando nunca houve escolha.
 *
 * O padrao e CLARO de proposito, e nao `sistema`: um visitante de primeira
 * viagem ve o site claro, qualquer que seja a configuracao do aparelho. Quem
 * prefere acompanhar o sistema escolhe "Automatico" explicitamente.
 */
function lerTema(): Tema {
  try {
    const salvo = localStorage.getItem(CHAVE_TEMA)
    if (salvo === 'claro' || salvo === 'escuro' || salvo === 'sistema') return salvo
  } catch {
    // localStorage bloqueado (navegacao privada, politica do dispositivo).
  }
  return 'claro'
}

/** Tema efetivamente em vigor - resolve `sistema` pela preferencia do aparelho. */
function temaEfetivo(tema: Tema): 'claro' | 'escuro' {
  if (tema === 'escuro') return 'escuro'
  if (tema === 'sistema') {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'escuro' : 'claro'
  }
  return 'claro'
}

/**
 * Preferencia de tema do usuario.
 *
 * Tres estados: `claro` (padrao), `escuro` e `sistema`. O terceiro existe para
 * quem quer acompanhar o aparelho - nao e o comportamento padrao, porque o
 * padrao do site e claro.
 *
 * A aplicacao inicial acontece em `public/tema.js`, antes da primeira pintura;
 * mantenha a logica dos dois em acordo. Este hook cuida da troca em tempo de
 * execucao e da persistencia.
 */
export function useTema() {
  const [tema, setTemaEstado] = useState<Tema>(lerTema)

  const aplicar = useCallback((novo: Tema) => {
    // 'sistema' tambem marca o atributo: e ele que habilita a media query no
    // CSS. Removido, o tema claro valeria.
    document.documentElement.setAttribute('data-tema', novo)

    const barra = document.querySelector('meta[name="theme-color"]')
    barra?.setAttribute('content', COR_DA_BARRA[temaEfetivo(novo)])

    try {
      localStorage.setItem(CHAVE_TEMA, novo)
    } catch {
      // Sem persistencia, a escolha vale apenas nesta visita.
    }
  }, [])

  const definirTema = useCallback(
    (novo: Tema) => {
      setTemaEstado(novo)
      aplicar(novo)
    },
    [aplicar],
  )

  // Em "Automatico", acompanha a troca do aparelho para a barra do navegador
  // seguir junto - o CSS ja acompanha pela media query.
  useEffect(() => {
    if (tema !== 'sistema') return
    const consulta = window.matchMedia('(prefers-color-scheme: dark)')
    function aoMudarSistema() {
      const barra = document.querySelector('meta[name="theme-color"]')
      barra?.setAttribute('content', COR_DA_BARRA[temaEfetivo('sistema')])
    }
    consulta.addEventListener('change', aoMudarSistema)
    return () => consulta.removeEventListener('change', aoMudarSistema)
  }, [tema])

  // Sincroniza entre abas: trocar o tema numa aba reflete nas outras.
  useEffect(() => {
    function aoMudarArmazenamento(evento: StorageEvent) {
      if (evento.key !== CHAVE_TEMA) return
      const atual = lerTema()
      setTemaEstado(atual)
      aplicar(atual)
    }
    window.addEventListener('storage', aoMudarArmazenamento)
    return () => window.removeEventListener('storage', aoMudarArmazenamento)
  }, [aplicar])

  return { tema, definirTema }
}

/**
 * Escala de fonte.
 *
 * Separada do tema porque atende outra necessidade: parte da comunidade tem
 * dificuldade de leitura e nao sabe usar o zoom do navegador. Um controle
 * visivel resolve sem exigir conhecimento tecnico.
 */
export function useEscalaDeTexto() {
  const [escala, setEscalaEstado] = useState(1)

  useEffect(() => {
    try {
      const salva = Number(localStorage.getItem(CHAVE_ESCALA))
      if (ESCALAS_DE_TEXTO.some((opcao) => opcao.valor === salva)) setEscalaEstado(salva)
    } catch {
      // Sem preferencia salva: fica no tamanho normal.
    }
  }, [])

  const definirEscala = useCallback((valor: number) => {
    setEscalaEstado(valor)
    document.documentElement.style.setProperty('--escala-texto', String(valor))
    try {
      localStorage.setItem(CHAVE_ESCALA, String(valor))
    } catch {
      // Sem persistencia, vale apenas nesta visita.
    }
  }, [])

  return { escala, definirEscala }
}
